import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { m } from 'motion/react'
import { useSEO } from '@/hooks/useSEO'
import { useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { ArrowRight, Calendar } from 'lucide-react'
import { blogPosts } from '@/lib/blog'
import { coverSrc } from '@/lib/coverCredits'

// Case studies with custom TSX pages (keep as hardcoded entries).
// `cover` is optional and overrides the conventional /images/blog/covers/<slug>.webp
// path. Use it when the art is hosted on the media CDN instead of committed to git
// (docs/conventions/site-media-in-s3.md) — the repo runs a shrink-only media budget,
// so new blog art belongs in S3.
const caseStudies = [
  {
    title: 'Sacred Music by Women Composers, Now Searchable by the Sunday It Serves',
    slug: 'great-host-composers-case-study',
    date: '2026-08-06',
    cover: 'https://images.fuguelab.app/media/case-studies/great-host-composers/blog/cover.webp',
    excerpt:
      'A Great Host of Composers spent three years curating sacred choral and organ music by women composers into a 17-tab spreadsheet a search engine could not read. Now it is 842 works by 265 composers, indexed to the church year across 1,312 pages Google can read.',
  },
  {
    title: 'Putting a Free Jazz Festival’s Lineup Back on Stage',
    slug: 'brass-city-jazz-case-study',
    date: '2026-06-24',
    excerpt:
      'A redesign concept for Brass City Jazz Fest: a fast, cinematic festival site where the Billboard-charting lineup leads and the sponsors who keep it free are finally visible. Full Score 62 → 94.',
  },
  {
    title: 'How We Moved a Church Off WordPress Without Touching Their Email',
    slug: 'holy-trinity-case-study',
    date: '2026-06-20',
    excerpt:
      'A bespoke rebuild for Holy Trinity Covina: off WordPress, onto a fast static site scoring 96/100 on The Full Score, with the parish email untouched through the cutover.',
  },
  {
    title: 'How We Built a Digital Photo Competition for Sierra Canyon School',
    slug: 'sierra-canyon-case-study',
    date: '2025-03-03',
    excerpt: 'Behind the scenes of replacing a paper-based photo competition with a mobile-first digital platform.',
  },
  {
    title: '5 Ways Classical Music Festivals Lose Applicants Online',
    slug: 'festivals-losing-applicants',
    date: '2025-01-15',
    excerpt: 'Common website and funnel mistakes that cost festivals qualified applicants, and how to fix them.',
  },
  {
    title: 'Case Study: How Chroma Festival Modernized Their Digital Presence',
    slug: 'chroma-festival-case-study',
    date: '2024-12-20',
    excerpt: 'Behind the scenes of building a modern festival website with application funnels and CRM integration.',
  },
]

export default function BlogPage() {
  const breadcrumbs = generateBreadcrumbs('/blog', breadcrumbLabels)

  // Merge markdown posts with hardcoded case studies, sorted by date
  const posts = useMemo(() => {
    const mdPosts = blogPosts.map((p) => ({
      title: p.title,
      slug: p.slug,
      date: p.date,
      excerpt: p.description,
      cover: p.featured_image,
    }))
    return [...mdPosts, ...caseStudies].sort((a, b) => b.date.localeCompare(a.date))
  }, [])

  const heroRef = useHeroReveal<HTMLElement>(0.2)
  const postsRef = useStaggeredGrid<HTMLElement>('.blog-card', { stagger: 0.1 })

  useSEO({
    title: 'Insights & Resources',
    description:
      'Practical guides, case studies, and digital strategy insights for classical music festival leaders navigating websites, application systems, and enrollment growth.',
    url: '/blog',
    canonical: '/blog',
    breadcrumbs,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Fugue Lab Blog',
      description: 'Digital strategy insights for classical music organizations',
      url: 'https://fuguelab.com/blog',
      publisher: {
        '@type': 'Organization',
        name: 'Fugue Lab',
        url: 'https://fuguelab.com',
      },
    },
  })

  return (
    <div className="mx-auto max-w-4xl space-y-12 px-4 py-12">
      <section ref={heroRef} className="space-y-4 text-center">
        <h1 className="text-fluid-h1">Insights &amp; Resources</h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed">
          Practical guides, case studies, and digital strategy insights for classical music festival leaders navigating
          websites, application systems, and enrollment growth.
        </p>
      </section>

      <section ref={postsRef} className="grid gap-6">
        {posts.map((post) => (
          <m.div
            key={post.slug}
            className="blog-card"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
          >
            <Link
              to={`/blog/${post.slug}`}
              className="group border-border/50 from-primary/5 hover:border-primary/40 block cursor-pointer overflow-hidden rounded-2xl border bg-linear-to-b to-transparent transition-all duration-300 hover:shadow-lg"
            >
              <img
                src={post.cover ?? coverSrc(post.slug)}
                alt=""
                width={1600}
                height={1000}
                loading="lazy"
                className="aspect-[16/10] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <div className="space-y-3 p-6">
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4" />
                  <span>{post.date}</span>
                </div>
                <h2 className="group-hover:text-primary text-xl font-semibold transition-colors">{post.title}</h2>
                <p className="text-muted-foreground leading-relaxed">{post.excerpt}</p>
                <span className="text-primary inline-flex items-center gap-1 text-sm font-medium transition-transform duration-300 group-hover:translate-x-1">
                  Read more <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          </m.div>
        ))}
      </section>
    </div>
  )
}
